import { lazy, memo, Suspense, useMemo } from 'react';

import { Skeleton } from '@mint-lib/tw-components';

import JourneyProviders from '../../../constants/JourneyProviders.js';
import { useAllIntegrations } from '../../../hooks/useAllIntegrations.js';
import { useIntegrationLayoutRouteApi } from '../../../hooks/useIntegrationsRouteApi.js';
import { useSelectedPlatform } from '../../../hooks/useSelectedPlatform.js';
import { Account, PlatformName } from '../../../types/common.js';
import AccountList from '../AccountList/AccountList.jsx';

const AdFormConnectForm = lazy(
  () => import('../../features/AdFormConnectForm/AdFormConnectForm.jsx'),
);
const CampaignManagerConnectForm = lazy(
  () =>
    import('../../features/CampaignManagerConnectForm/CampaignManagerConnectForm.jsx'),
);
const GoogleAnalyticsConnectForm = lazy(
  () =>
    import('../../features/GoogleAnalyticsConnectForm/GoogleAnalyticsConnectForm.jsx'),
);
const AppsFlyerConnectForm = lazy(
  () => import('../../features/AppsFlyerConnectForm/AppsFlyerConnectForm.jsx'),
);

const AccountsTab = () => {
  const selectedPlatform = useSelectedPlatform();
  const accountsList = useAllIntegrations();

  const { integration: routeIntegration } =
    useIntegrationLayoutRouteApi().useParams();

  const integration = useMemo(
    () => (routeIntegration === 'ga4' ? 'google_analytics' : routeIntegration),
    [routeIntegration],
  );

  const CustomForm = useMemo(() => {
    const connectFormComponents = {
      [JourneyProviders.ADFORM_AD_SERVER]: AdFormConnectForm,
      [JourneyProviders.CM]: CampaignManagerConnectForm,
      [JourneyProviders.GA4]: GoogleAnalyticsConnectForm,
      [JourneyProviders.APPSFLYER]: AppsFlyerConnectForm,
    };

    const ConnectFormComponent = connectFormComponents[integration];

    if (ConnectFormComponent) {
      return (
        <Suspense
          fallback={
            <Skeleton
              variant="rounded"
              className="mt-2 h-[369px] w-full max-w-[909px]"
            />
          }
        >
          <ConnectFormComponent />
        </Suspense>
      );
    }
    return null;
  }, [integration]);

  return (
    <>
      <div className="box-border h-full max-w-[500px] bg-ui-secondary p-0">
        <AccountList
          list={
            accountsList[integration as PlatformName] as unknown as Account[]
          }
          integration={integration}
          logo={selectedPlatform?.logo ?? ''}
          isListLoading={
            selectedPlatform === null || accountsList.loading[integration]
          }
          disableAddAccountButton={false}
        />
      </div>
      <div className="w-full min-w-0">{CustomForm}</div>
    </>
  );
};

export default memo(AccountsTab);
